import { useEffect, useState } from "react";

import axios from "axios";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "@platform/constants/shared-constants";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface EnvironmentInfo {
  id: number;
  name: string;
  slug: string;
  envType: string;
  status: string;
  projectId: number;
  organizationId: number;
}

export interface StackInfo {
  id: number;
  alias: string;
  name: string;
  status: string;
  deployMode: string;
  templateSlug: string | null;
  vcsRepoUrl: string | null;
  environmentId: number | null;
}

// ── Data Hooks ─────────────────────────────────────────────────────────────────

export function useEnvironments(orgAlias: string, projectAlias: string) {
  const [environments, setEnvironments] = useState<EnvironmentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = orgSessionManager.getToken() ?? "";
    setLoading(true);
    axios
      .get<EnvironmentInfo[]>(`${apiBaseUrls.BACKEND}/api/v1/environments`, {
        params: { projectAlias },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setEnvironments(res.data))
      .catch(() => setError("Failed to load environments"))
      .finally(() => setLoading(false));
  }, [orgAlias, projectAlias]);

  return { environments, loading, error };
}

export function useStacksForProject(orgAlias: string, projectAlias: string) {
  const [stacks, setStacks] = useState<StackInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = orgSessionManager.getToken() ?? "";
    setLoading(true);
    axios
      .get<StackInfo[]>(`${apiBaseUrls.BACKEND}/api/v1/applications`, {
        params: { projectAlias },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setStacks(res.data))
      .catch(() => setStacks([]))
      .finally(() => setLoading(false));
  }, [orgAlias, projectAlias]);

  return { stacks, loading };
}
